import { useState, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { OrgFundraiserOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  Icon,
  PageHeader,
  Tabs,
  type DataTableColumn,
} from '@givmo/design-system';
import { Plus } from 'lucide-react';
import { useFundraisersModule } from './fundraisers-context';
import { mayReadFundraisers, mayWriteFundraisers } from './fundraisers-config';
import { displayCount, displayDate, fundraiserBadge } from './fundraisers-decode';
import { FUNDRAISERS_ROUTES, type FundraisersView } from './fundraisers-routes';
import { PORTFOLIO_PAGE_SIZE, portfolioOptions, type PortfolioState } from './fundraisers-queries';
import {
  FundraisersLoading,
  FundraisersPageShell,
  FundraisersReadState,
  ModuleLink,
} from './fundraisers-state';
import { GoalThermometer } from './goal-thermometer';
import { REQUESTS_COPY } from './join-requests-copy';
import {
  SUPPORTER_FUNDRAISERS_COPY,
  SupporterFundraisersPanel,
  SupporterTabCount,
  supporterCountLabel,
  useSupporterFundraisers,
} from './supporter-fundraisers';

const FILTERS: readonly { value: PortfolioState; label: string }[] = [
  { value: 'all', label: 'All fundraisers' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Drafts' },
  { value: 'closed', label: 'Closed' },
];

/** direct_raised is emitted only for an upgraded umbrella, including a zero total. */
export function isCampaign(row: OrgFundraiserOut): boolean {
  return row.stats.direct_raised != null;
}

/**
 * FCM-WEB33 (decision 1, frame 04): a campaign's requests to join waiting for an answer, people's and
 * campaigns' together, where the organizer lands. The server sends the count only while some wait.
 */
function RequestsWaiting({ row }: { row: OrgFundraiserOut }) {
  const count = row.pending_requests;
  if (typeof count !== 'number' || !Number.isSafeInteger(count) || count <= 0) return null;
  return (
    <Badge tone="brand" testId={`fundraiser-requests-${row.id}`}>
      {REQUESTS_COPY.listCount(count)}
    </Badge>
  );
}

const COLUMNS: DataTableColumn<OrgFundraiserOut>[] = [
  {
    id: 'name',
    header: 'Fundraiser',
    width: '36%',
    stacked: 'heading',
    cell: (row) => (
      <div className="flex min-w-0 flex-col gap-4">
        <span className="font-w600 text-text-strong">{row.name}</span>
        {isCampaign(row) && (
          <span className="flex flex-wrap items-center gap-x-8 gap-y-4 text-s12 text-text-secondary">
            Campaign
            <RequestsWaiting row={row} />
          </span>
        )}
      </div>
    ),
  },
  {
    id: 'state',
    header: 'Status',
    stacked: 'meta',
    // FCM-WEB28: "Taken down" (red) for a Givmo takedown, off the row's `lifecycle` (ruling Q3).
    cell: (row) => {
      const badge = fundraiserBadge(row);
      return <Badge tone={badge.tone}>{badge.label}</Badge>;
    },
  },
  {
    id: 'progress',
    header: 'Raised',
    width: '25%',
    stacked: 'line',
    stackedLabel: 'Raised',
    cell: (row) => (
      <GoalThermometer
        compact
        raised={row.stats.total_raised}
        goal={row.goal_amount}
        percent={row.goal_progress_percent}
      />
    ),
  },
  {
    id: 'donations',
    header: 'Donations',
    align: 'right',
    numeric: true,
    cell: (row) => displayCount(row.stats.donation_count),
  },
  {
    id: 'created',
    header: 'Created',
    stacked: 'line',
    stackedLabel: 'Created',
    cell: (row) => <span className="text-text-secondary">{displayDate(row.created_at)}</span>,
  },
];

/**
 * The Fundraisers page. With the flavor's `supporterFundraisers` it carries two page tabs — "Your
 * fundraisers" and "Started by supporters" — the view riding the list route's search (`?tab=`), which
 * the host hands in as `view`; the own list's state filters then become chips, so two underline rows
 * never stack. Without it the page is the organization's own list under its filter tabs, unchanged.
 */
export function FundraisersPage({ view = 'own' }: { view?: FundraisersView }) {
  const config = useFundraisersModule();
  // Remount this state when the host changes organization; a late response is
  // still isolated by its generated query key and explicit org_ref check.
  return <Portfolio key={config.orgRef ?? 'pending'} view={view} />;
}

function Portfolio({ view }: { view: FundraisersView }) {
  const config = useFundraisersModule();
  const [state, setState] = useState<PortfolioState>('all');
  const [offset, setOffset] = useState(0);
  const [supporterOffset, setSupporterOffset] = useState(0);
  const readable = mayReadFundraisers(config);
  const supporterTab = config.flavor.supporterFundraisers === true;
  const query = useQuery({
    ...portfolioOptions(config.orgRef ?? '', state, offset),
    enabled: readable,
  });
  // The tab's count reads PAGE ONE's own cache entry, so paging the list never moves or drops it.
  const supporterCount = useSupporterFundraisers(0, supporterTab && readable);
  const chooseState = (value: PortfolioState) => {
    setState(value);
    setOffset(0);
  };
  const newAction = mayWriteFundraisers(config) ? (
    <Button
      appearance="saas"
      leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
      onClick={() => config.onNavigate(FUNDRAISERS_ROUTES.create)}
      testId="fundraiser-new"
    >
      New fundraiser
    </Button>
  ) : undefined;
  const ownList = (
    <OwnFundraisers
      query={query}
      state={state}
      offset={offset}
      onOffset={setOffset}
      newAction={newAction}
    />
  );
  // FCM-WEB5: an empty "all" list carries its own New fundraiser in the empty state, so the header's
  // steps aside while that state is on screen — one control, one `fundraiser-new`. It decides on the
  // resolved list alone, so both never render in the same commit.
  const emptyStateOffersNew =
    (!supporterTab || view === 'own') &&
    state === 'all' &&
    query.isSuccess &&
    query.data.length === 0;
  return (
    <FundraisersPageShell testId="page-fundraisers">
      <PageHeader title="Fundraisers" actions={emptyStateOffersNew ? undefined : newAction} />
      {!config.orgRef ? (
        <FundraisersLoading />
      ) : !readable ? (
        <FundraisersReadState denied onRetry={() => void query.refetch()} />
      ) : supporterTab ? (
        <Tabs.Root
          appearance="saas"
          value={view}
          onValueChange={(value) =>
            config.onNavigate(
              value === 'supporters' ? FUNDRAISERS_ROUTES.supporters : FUNDRAISERS_ROUTES.list,
            )
          }
        >
          <Tabs.List aria-label="Fundraisers" className="mb-16">
            <Tabs.Tab value="own" testId="fundraisers-tab-own">
              {SUPPORTER_FUNDRAISERS_COPY.ownTab}
            </Tabs.Tab>
            <Tabs.Tab value="supporters" testId="fundraisers-tab-supporters">
              <span className="inline-flex items-center gap-6">
                {SUPPORTER_FUNDRAISERS_COPY.tab}
                <SupporterTabCount
                  label={supporterCount.isSuccess ? supporterCountLabel(supporterCount.data) : null}
                />
              </span>
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="own" focusable={false} className="pt-0">
            {query.isError ? (
              <FundraisersReadState error={query.error} onRetry={() => void query.refetch()} />
            ) : (
              <>
                <FilterBar
                  ariaLabel="Filter your fundraisers"
                  className="mb-16"
                  testId="fundraisers-filters"
                >
                  {FILTERS.map((filter) => (
                    <FilterChip
                      key={filter.value}
                      label={filter.label}
                      selected={state === filter.value}
                      onToggle={() => chooseState(filter.value)}
                      testId={`fundraisers-filter-${filter.value}`}
                    />
                  ))}
                </FilterBar>
                {ownList}
              </>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="supporters" focusable={false} className="pt-0">
            <SupporterFundraisersPanel offset={supporterOffset} onOffset={setSupporterOffset} />
          </Tabs.Panel>
        </Tabs.Root>
      ) : query.isError ? (
        <FundraisersReadState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <Tabs.Root
          appearance="saas"
          value={state}
          onValueChange={(value) => chooseState(value as PortfolioState)}
        >
          <Tabs.List aria-label="Filter fundraisers" className="mb-16">
            {FILTERS.map((filter) => (
              <Tabs.Tab
                key={filter.value}
                value={filter.value}
                testId={`fundraisers-filter-${filter.value}`}
              >
                {filter.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {ownList}
        </Tabs.Root>
      )}
    </FundraisersPageShell>
  );
}

/** The organization's own list under whichever filter control the page wears. */
function OwnFundraisers({
  query,
  state,
  offset,
  onOffset,
  newAction,
}: {
  query: UseQueryResult<OrgFundraiserOut[], Error>;
  state: PortfolioState;
  offset: number;
  onOffset: (update: (current: number) => number) => void;
  newAction: ReactNode;
}) {
  const config = useFundraisersModule();
  const rows = query.data?.slice(0, PORTFOLIO_PAGE_SIZE) ?? [];
  const hasNext = (query.data?.length ?? 0) > PORTFOLIO_PAGE_SIZE;
  if (query.isPending) return <FundraisersLoading />;
  return (
    <>
      {rows.length === 0 ? (
        <EmptyState
          testId="fundraisers-empty"
          title={state === 'all' ? 'Your fundraisers start here' : `No ${state} fundraisers`}
          description={
            state === 'all'
              ? 'Create a fundraiser for your organization and share it with supporters.'
              : undefined
          }
          action={state === 'all' ? newAction : undefined}
        />
      ) : (
        <>
          <div className="hidden lg:block">
            <DataTable
              testId="fundraisers-table"
              columns={COLUMNS}
              rows={rows}
              rowKey={(row) => row.id}
              rowTestId={(row) => `fundraiser-row-${row.id}`}
              onRowClick={(row) => config.onNavigate(FUNDRAISERS_ROUTES.editor(row.id))}
              stackAt="lg"
            />
          </div>
          <div className="flex flex-col gap-12 lg:hidden" data-testid="fundraisers-cards">
            {rows.map((row) => (
              <Card key={row.id} flush testId={`fundraiser-card-${row.id}`}>
                <CardBody className="flex flex-col gap-16 p-16">
                  <div className="flex items-start justify-between gap-12">
                    <div className="min-w-0">
                      <ModuleLink
                        href={FUNDRAISERS_ROUTES.editor(row.id)}
                        testId={`fundraiser-open-${row.id}`}
                        className="inline-flex min-h-44 items-center whitespace-normal text-left text-s16 font-w600 text-text-strong hover:underline"
                      >
                        {row.name}
                      </ModuleLink>
                      {isCampaign(row) && (
                        <p className="mt-2 flex flex-wrap items-center gap-x-8 gap-y-4 text-s12 text-text-secondary">
                          Campaign
                          <RequestsWaiting row={row} />
                        </p>
                      )}
                    </div>
                    <Badge tone={fundraiserBadge(row).tone} className="mt-10 shrink-0">
                      {fundraiserBadge(row).label}
                    </Badge>
                  </div>
                  <div>
                    <p className="mb-6 text-s12 text-text-secondary">Raised</p>
                    <GoalThermometer
                      compact
                      raised={row.stats.total_raised}
                      goal={row.goal_amount}
                      percent={row.goal_progress_percent}
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-x-12 gap-y-4 border-t border-border-subtle pt-12 text-s12 text-text-secondary">
                    {/* "1 donation" for one (FCM-WEB43's sibling); still two text nodes. */}
                    <span>
                      {displayCount(row.stats.donation_count)}
                      {row.stats.donation_count === 1 ? ' donation' : ' donations'}
                    </span>
                    <span>Created {displayDate(row.created_at)}</span>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        </>
      )}
      {(offset > 0 || hasNext) && (
        <div
          className="mt-16 flex items-center justify-between gap-12"
          data-testid="fundraisers-pagination"
        >
          <Button
            appearance="saas"
            tone="secondary"
            disabled={offset === 0}
            onClick={() => onOffset((current) => Math.max(0, current - PORTFOLIO_PAGE_SIZE))}
            testId="fundraisers-previous"
          >
            Previous
          </Button>
          <span className="text-s13 text-text-secondary">
            Page {Math.floor(offset / PORTFOLIO_PAGE_SIZE) + 1}
          </span>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={!hasNext}
            onClick={() => onOffset((current) => current + PORTFOLIO_PAGE_SIZE)}
            testId="fundraisers-next"
          >
            Next
          </Button>
        </div>
      )}
    </>
  );
}
